import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    CheckCircle2,
    Eye,
    EyeOff,
    KeyRound,
    Loader2,
    Lock,
    ShieldCheck,
    X,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";
import config from "../config";
import { useAuth } from "../context/AuthContext";

const INITIAL_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" };
const RULES = [
    { label: "8+ characters", test: (value) => value.length >= 8 },
    { label: "Uppercase", test: (value) => /[A-Z]/.test(value) },
    { label: "Lowercase", test: (value) => /[a-z]/.test(value) },
    { label: "Number", test: (value) => /\d/.test(value) },
    { label: "Symbol", test: (value) => /[^A-Za-z0-9\s]/.test(value) },
];

function passwordStrength(password) {
    const rules = RULES.map((rule) => ({ ...rule, passed: rule.test(password) }));
    const score = rules.filter((rule) => rule.passed).length;
    if (!password) return { score, label: "Create a new password", bar: "bg-border", text: "text-muted-foreground", rules };
    if (score <= 2) return { score, label: "Weak", bar: "bg-red-500", text: "text-red-500", rules };
    if (score <= 4) return { score, label: "Good", bar: "bg-amber-500", text: "text-amber-500", rules };
    return { score, label: "Strong", bar: "bg-emerald-500", text: "text-emerald-500", rules };
}

async function readJson(response) {
    const text = await response.text();
    if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}.`);
    try { return JSON.parse(text); }
    catch {
        console.error("Invalid change-password response:", text);
        throw new Error("The server returned an invalid response. Check the PHP error log.");
    }
}

export default function ChangePassword({ endpoint = `${config.API_URL}/password_change.php` }) {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [visible, setVisible] = useState({ currentPassword: false, newPassword: false, confirmPassword: false });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");

    const strength = useMemo(() => passwordStrength(form.newPassword), [form.newPassword]);
    const hasConfirmation = form.confirmPassword.length > 0;
    const passwordsMatch = hasConfirmation && form.newPassword === form.confirmPassword;

    const updateField = ({ target: { name, value } }) => {
        setForm((current) => ({ ...current, [name]: value }));
        setErrors((current) => ({ ...current, [name]: "" }));
        setSuccess("");
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.currentPassword) nextErrors.currentPassword = "Please enter your current password.";
        if (strength.score !== RULES.length) nextErrors.newPassword = "Please complete all password requirements.";
        else if (form.currentPassword === form.newPassword) nextErrors.newPassword = "New password must be different from the current password.";
        if (!form.confirmPassword) nextErrors.confirmPassword = "Please confirm your new password.";
        else if (!passwordsMatch) nextErrors.confirmPassword = "Passwords do not match.";
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (loading || !validate()) return;
        setLoading(true);
        setSuccess("");

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    current_password: form.currentPassword,
                    new_password: form.newPassword,
                    confirm_password: form.confirmPassword,
                }),
            });
            const result = await readJson(response);

            if (!response.ok || !result.success) {
                if (result.errors && typeof result.errors === "object") setErrors(result.errors);
                throw new Error(result.message || "Password could not be changed.");
            }

            const message = result.message || "Password changed successfully. Please sign in again.";
            setSuccess(message);
            toast.success(message);

            window.setTimeout(() => {
                logout();
                navigate(result.redirect || "/login", { replace: true });
            }, 1500);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Unable to connect to the server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
            <div className="pointer-events-none absolute inset-0 flex justify-center">
                <div className="mt-10 h-[500px] w-[500px] rounded-full bg-gradient-to-tr from-[#3BC7FF]/20 to-[#7B61FF]/20 blur-3xl" />
            </div>

            <div className="relative mx-auto w-full max-w-lg fade-in-up">
                <button type="button" onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Back
                </button>

                <section className="glass-card rounded-3xl border border-border/70 p-6 shadow-xl sm:p-8">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl btn-gradient text-white shadow-lg">
                            <KeyRound className="h-6 w-6" />
                        </div>
                        <h1 className="mt-4 text-2xl font-display font-bold tracking-tight sm:text-3xl">
                            Change your <span className="text-gradient-brand">password</span>
                        </h1>
                        <p className="mt-2 text-sm text-muted-foreground">Choose a strong password you have not used before.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-7 space-y-5" noValidate>
                        <PasswordField id="currentPassword" label="Current Password" value={form.currentPassword} error={errors.currentPassword} visible={visible.currentPassword} loading={loading} placeholder="Enter your current password" onChange={updateField} onToggle={() => setVisible((current) => ({ ...current, currentPassword: !current.currentPassword }))} />

                        <div>
                            <PasswordField id="newPassword" label="New Password" value={form.newPassword} error={errors.newPassword} visible={visible.newPassword} loading={loading} placeholder="Create a strong new password" onChange={updateField} onToggle={() => setVisible((current) => ({ ...current, newPassword: !current.newPassword }))} />

                            <div className="mt-3 rounded-2xl border border-border/70 bg-background/35 p-3.5">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex flex-1 gap-1.5">
                                        {[1, 2, 3, 4, 5].map((step) => <span key={step} className={`h-1.5 flex-1 rounded-full transition-colors ${step <= strength.score ? strength.bar : "bg-border"}`} />)}
                                    </div>
                                    <span className={`text-xs font-semibold ${strength.text}`}>{strength.label}</span>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                                    {strength.rules.map((rule) => (
                                        <span key={rule.label} className={`inline-flex items-center gap-1 text-[11px] ${rule.passed ? "text-emerald-500" : "text-muted-foreground"}`}>
                                            {rule.passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{rule.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div>
                            <PasswordField id="confirmPassword" label="Confirm New Password" value={form.confirmPassword} error={errors.confirmPassword} visible={visible.confirmPassword} loading={loading} placeholder="Enter the new password again" onChange={updateField} onToggle={() => setVisible((current) => ({ ...current, confirmPassword: !current.confirmPassword }))} status={hasConfirmation ? (passwordsMatch ? "success" : "error") : ""} />
                            {hasConfirmation && <p className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${passwordsMatch ? "text-emerald-500" : "text-red-500"}`}>{passwordsMatch ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{passwordsMatch ? "Passwords match" : "Passwords do not match"}</p>}
                        </div>

                        {success && <div className="flex items-start gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-600" role="status"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{success}</div>}

                        <button type="submit" disabled={loading || Boolean(success)} className="w-full h-12 rounded-full btn-gradient ripple text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-65 disabled:cursor-not-allowed">
                            {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Changing password…</> : <><ShieldCheck className="h-4 w-4" />Change Password</>}
                        </button>
                    </form>
                </section>

                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.26em] text-muted-foreground">Secured by Smart Solutions</p>
            </div>
        </main>
    );
}

function PasswordField({ id, label, value, error, visible, loading, placeholder, onChange, onToggle, status }) {
    return (
        <div>
            <label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
            <div className="relative mt-2">
                <Lock className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${error || status === "error" ? "text-red-500" : status === "success" ? "text-emerald-500" : "text-muted-foreground"}`} />
                <input id={id} name={id} type={visible ? "text" : "password"} value={value} disabled={loading} onChange={onChange} autoComplete={id === "currentPassword" ? "current-password" : "new-password"} placeholder={placeholder} className={`h-12 w-full rounded-xl border bg-background/60 pl-11 pr-11 text-sm outline-none transition focus:ring-2 ${error || status === "error" ? "border-red-500 focus:ring-red-500/20" : status === "success" ? "border-emerald-500 focus:ring-emerald-500/20" : "border-border focus:border-primary focus:ring-primary/25"}`} />
                <button type="button" onClick={onToggle} disabled={loading} aria-label={visible ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground disabled:opacity-50">
                    {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
            </div>
            {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
        </div>
    );
}
